import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import { Button, StepProgress } from '@/design-system'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '../components/EgowallLogo'

export type CreateAlbumLayoutContext = {
  step: number
  setStep: (n: number) => void
}

const Root = styled(Box)({
  minHeight: '100vh',
  background: 'var(--ds-surface)',
  color: 'var(--ds-ink)',
  fontFamily: 'var(--ds-font)',
})

const Top = styled(Box)({
  height: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '0 28px',
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--ds-border)',
  position: 'sticky',
  top: 0,
  zIndex: 40,
})

const LogoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  cursor: 'pointer',
  userSelect: 'none',
  flexShrink: 0,
})

const LogoText = styled(Typography)({
  fontSize: 16.5,
  fontWeight: 750,
  letterSpacing: '-0.03em',
  color: 'var(--ds-ink)',
})

const STEP_LABELS = ['NAME', 'PHOTOS', 'KEY']

/** Focused create chrome — logo, spectrum steps, cancel. No library sidebar. */
export function CreateAlbumLayout() {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const ctx = useMemo(() => ({ step, setStep }), [step])

  return (
    <Root>
      <Top>
        <LogoRow
          onClick={() => navigate('/albums')}
          role='link'
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/albums')}
        >
          <LogoMark />
          <LogoText>{APP_NAME}</LogoText>
        </LogoRow>
        <StepProgress
          current={step}
          total={3}
          labels={STEP_LABELS}
          tone='spectrum'
          segmentWidth={34}
          sx={{ flexShrink: 1, minWidth: 0 }}
        />
        <Button variant='ghost' size='sm' onClick={() => navigate('/albums')}>
          Cancel
        </Button>
      </Top>
      <Outlet context={ctx} />
    </Root>
  )
}

export function useCreateAlbumLayout() {
  return useOutletContext<CreateAlbumLayoutContext>()
}
